<template>
    <el-dialog
        ref="DetailDialog"
        class="financial-analysis-item-list-dialog ah-detail-dialog"
        top="10vh"
        append-to-body
        :visible="visible"
        :modal-append-to-body="false"
        @open="onOpen"
        @close="onClose"
    >
        <template slot="title">財務資產收支</template>
        <div class="dialog-body">
            <div class="dialog-header">
                <div class="name">{{ itemName }}</div>
                <div>
                    <span>{{ parseInt(startDate, 10) | dateFormater("YYYY-MM-dd") }}</span>
                    <span> ~ </span>
                    <span>{{ parseInt(endDate, 10) | dateFormater("YYYY-MM-dd") }}</span>
                </div>
                <div>共 {{ pagination.totalCount }} 筆資料</div>
            </div>
            <div>
                <el-table
                    v-loading="isLoading"
                    :data="list"
                    style="width: 100%"
                    height="400px"
                    size="small"
                    class="ah-el-data-table row-click list-table"
                    @row-click="handleDataRowClick"
                    empty-text="沒有資料"
                >
                    <el-table-column prop="systemNo" label="編號" width="110">
                        <template slot-scope="scope">
                            <span class="idx">{{ scope.row.systemNo || scope.row.moo || scope.row.payoo }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="flag" label="類型" width="80">
                        <template slot-scope="scope">
                            <span v-if="scope.row.flag === -1" class="ah-status payout"> 支出</span>
                            <span v-if="scope.row.flag === 1" class="ah-status income">收入</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="date" label="繳費/收款日期" width="120">
                        <template slot-scope="scope">
                            <span>{{ scope.row.dat | dateFormater("YYYY-MM-dd") }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="nam" label="摘要" min-width="200">
                        <template slot-scope="scope">
                            <div>{{ scope.row.nam || scope.row.tpc }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="amt" label="金額" width="110" align="right">
                        <template slot-scope="scope">
                            <Money :v="scope.row.amt" :precision="scope.row.cny === 'TWD' ? null : 4" />
                        </template>
                    </el-table-column>
                    <el-table-column prop="cny" label="幣別" width="70"></el-table-column>
                </el-table>
                <div style="display: flex; justify-content: center; padding: 10px 0;">
                    <el-pagination
                        layout="prev, pager, next"
                        hide-on-single-page
                        :page-size.sync="pagination.pageSize"
                        :total.sync="pagination.totalCount"
                        :current-page.sync="pagination.pageNum"
                        @current-change="fetchData"
                        class="pagination"
                    >
                    </el-pagination>
                </div>
            </div>
        </div>
        <VariableDetailDialog
            @close="variableDetailDialog.visible = false"
            :visible="variableDetailDialog.visible"
            :sno="variableDetailDialog.sno"
        />
        <PaymentDetailDialog
            @close="paymentDetailDialog.visible = false"
            :visible="paymentDetailDialog.visible"
            :sno="paymentDetailDialog.sno"
        />
    </el-dialog>
</template>
<script>
import VariableDetailDialog from '@/components/dashboard/Finance/VariableStatement/DetailDialog.vue';
import PaymentDetailDialog from '@/components/dashboard/Payment/PaymentDetailDialog.vue';

export default {
    components: {
        VariableDetailDialog,
        PaymentDetailDialog,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        startDate: {
            type: String,
            default: null,
        },
        endDate: {
            type: String,
            default: null,
        },
        sno: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            list: [],
            itemName: '',
            totalAmt: 0,
            pagination: {
                pageNum: 1,
                pageSize: 25,
                totalPages: 1,
                totalCount: 1,
            },
            variableDetailDialog: {
                visible: false,
                sno: null,
            },
            paymentDetailDialog: {
                visible: false,
                sno: null,
            },
        };
    },
    methods: {
        onOpen() {
            this.pagination.pageNum = 1;
            this.pagination.totalPages = 1;
            this.pagination.totalCount = 1;
            this.list = [];
            this.itemName = '';
            this.totalAmt = 0;
            this.fetchData();
        },
        onClose() {
            this.$emit('close');
        },
        async fetchData() {
            this.isLoading = true;

            try {
                const params = {
                    sno: this.sno,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    pageNum: this.pagination.pageNum,
                    pageSize: this.pagination.pageSize,
                };

                const [financeResp, paymentResp] = await Promise.all([
                    this.$api({
                        url: 'FinancialStatement/GetFinancelistByItem',
                        method: 'GET',
                        params,
                    }),
                    this.$api({
                        url: 'FinancialStatement/GetPaymentListByItem',
                        method: 'GET',
                        params,
                    }),
                ]);

                const financeData = financeResp && financeResp.data ? financeResp.data : {};
                const paymentData = paymentResp && paymentResp.data ? paymentResp.data : {};

                const financeList = financeData.list || [];
                const paymentList = paymentData.list || [];

                const normalizedFinance = financeList.map((item) => ({ ...item, isPayment: false }));
                const normalizedPayment = paymentList.map((item) => ({ ...item, isPayment: true }));

                const merged = [...normalizedFinance, ...normalizedPayment];
                merged.sort((a, b) => (b.dat || 0) - (a.dat || 0));

                this.list = merged;

                const financePag = financeData.pagination || {};
                const paymentPag = paymentData.pagination || {};

                this.pagination.pageNum = financePag.pageNum || paymentPag.pageNum || this.pagination.pageNum;
                this.pagination.pageSize = financePag.pageSize || paymentPag.pageSize || this.pagination.pageSize;
                this.pagination.totalCount = (financePag.totalCount || 0) + (paymentPag.totalCount || 0);
                this.pagination.totalPages = Math.max(financePag.totalPages || 0, paymentPag.totalPages || 0);

                this.itemName = financeData.itemName || paymentData.itemName || '';
                this.totalAmt = (financeData.totalAmt || 0) + (paymentData.totalAmt || 0);
            } catch (err) {
                console.error(err);
                this.$message.error('讀取明細資料失敗');
            } finally {
                this.isLoading = false;
            }
        },
        handleDataRowClick(row) {
            if (row.isPayment) {
                this.paymentDetailDialog.sno = row.sno;
                this.paymentDetailDialog.visible = true;
            } else {
                this.variableDetailDialog.sno = row.sno;
                this.variableDetailDialog.visible = true;
            }
        },
    },
};

</script>
<style lang="scss">
.financial-analysis-item-list-dialog {
    .el-dialog {
        max-width: 960px;
        margin-bottom: 10px !important;
        .dialog-body {
            padding: 20px;
            min-height: 350px;
        }
        .dialog-header {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            margin-bottom: 20px;
            font-size: 12pt;
            font-weight: bold;
        }
    }

    .list-table {
        border: 1px solid #ddd;
        border-radius: 6px;
    }
}
</style>
